import type { RateLineProposalListQuery } from '@chukua/common';
import { RATE_LINE_PROPOSAL_KINDS } from '@chukua/common';
import { Button, type TableColumnsType } from 'antd';
import { Link } from 'react-router';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import { ProposalDiff } from './ProposalDiff';
import { PROPOSAL_KIND_LABELS } from '../../../policies/rate-card-labels';
import { rateCardTabPath } from '../../../rate-cards.paths';
import type { RateLineProposalQueueView } from '../../../rate-cards.api';
import { formatLineKey } from '../../../policies/rate-line-key';

interface BuildQueueColumnsArgs {
  list: Pick<ListQueryControls<RateLineProposalListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  userId: string | undefined;
  onDecide: (proposal: RateLineProposalQueueView) => void;
}

/** Each column's `key` is its `sort` field or filter param in `rateLineProposalListQuerySchema`. */
export function buildQueueColumns({ list, userId, onDecide }: BuildQueueColumnsArgs): TableColumnsType<RateLineProposalQueueView> {
  return [
    {
      title: 'Rate card',
      key: 'rateCard',
      render: (_, proposal) => (
        <Link className="font-medium text-link hover:underline" to={rateCardTabPath(proposal.rateCardId, 'lines')}>
          {proposal.rateCardCustomerName ?? 'Standard rate card'}
        </Link>
      ),
    },
    { title: 'Line', key: 'line', render: (_, proposal) => formatLineKey(proposal) },
    {
      title: 'Kind',
      key: 'kind',
      filters: RATE_LINE_PROPOSAL_KINDS.map((kind) => ({ text: PROPOSAL_KIND_LABELS[kind], value: kind })),
      filteredValue: list.filteredValueFor('kind'),
      render: (_, proposal) => PROPOSAL_KIND_LABELS[proposal.kind],
    },
    { title: 'Change', key: 'change', render: (_, proposal) => <ProposalDiff proposal={proposal} /> },
    {
      title: 'Submitted',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      sortOrder: list.sortOrderFor('createdAt'),
      render: (_, proposal) => new Date(proposal.submittedAt ?? proposal.createdAt).toLocaleDateString(),
    },
    {
      title: '',
      key: 'actions',
      // Not offered on your own proposal: the approver can never be the author (separation of duties).
      render: (_, proposal) =>
        proposal.authorId === userId ? (
          <span className="text-xs text-muted">Your proposal</span>
        ) : (
          <Button size="small" type="primary" onClick={() => onDecide(proposal)}>
            Decide
          </Button>
        ),
    },
  ];
}
